import { Grid, MenuItem, TextField } from "@mui/material";
import { SlidersHorizontal, X } from "lucide-react";
import Button from "../common/Button";
import {
  aviationDepartments,
  experienceLevelChoices,
  jobTypes,
} from "../../pages/recruiter/components/dropDownData";
import Select from "../common/Select";

const salaryRanges = [
  { id: "under-30k", label: "Under $30K", min: 0, max: 30000 },
  { id: "30k-50k", label: "$30K - $50K", min: 30000, max: 50000 },
  { id: "50k-80k", label: "$50K - $80K", min: 50000, max: 80000 },
  { id: "80k-100k", label: "$80K - $100K", min: 80000, max: 100000 },
  { id: "over-100k", label: "Over $100K", min: 100000, max: null },
];

const SideFilters = ({
  filters,
  handleFilterChange,
  resetFilters,
  pilotTypes,
  aviationCategoryChoices,
  applyFilters,
}) => {
  // Count active filters for badge
  const activeFilterCount = [
    filters.pilot_type ? 1 : 0,
    filters.experience_level ? 1 : 0,
    filters.aviation_category ? 1 : 0,
    filters.total_flight_hours ? 1: 0,
    // filters.is_remote ? 1 : 0,
    filters.min_salary || filters.max_salary ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  // Handler for single selection (radio-like behavior)
  const handleSingleSelect = (filterType, value) => {
    if (filters[filterType] === value) {
      handleFilterChange(filterType, "");
    } else {
      handleFilterChange(filterType, value);
    }
  };

  // Handler for salary range selection
  const handleSalaryRangeSelect = (range) => {
    // Convert current filter values to numbers for comparison
    const currentMin = filters.min_salary ? Number(filters.min_salary) : null;
    const currentMax = filters.max_salary ? Number(filters.max_salary) : null;

    if (currentMin === range.min && currentMax === range.max) {
      // Deselect if same range is clicked
      handleFilterChange("min_salary", "");
      handleFilterChange("max_salary", "");
    } else {
      // Select new range - convert to strings to match URL param format
      handleFilterChange("min_salary", range.min.toString());
      handleFilterChange("max_salary", range.max ? range.max.toString() : "");
    }
  };


  const isSalaryRangeSelected = (range) => {
    const currentMin = filters.min_salary ? Number(filters.min_salary) : null;
    const currentMax = filters.max_salary ? Number(filters.max_salary) : null;

    return currentMin === range.min && currentMax === range.max;
  };

  // Custom salary range input handler
  const handleCustomSalaryChange = (field, value) => {
    handleFilterChange(field, value || "");
  };

  return (
    <aside className="lg:col-span-4 xl:col-span-3">
      <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 sticky top-24 transition-all duration-200 hover:shadow-xl">
        {/* Header with filter count badge */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <div className="flex items-center">
              <SlidersHorizontal className="w-5 h-5 mr-2 text-gray-700" />
              <h3 className="font-bold text-lg text-gray-900">Filters</h3>
            </div>
            {activeFilterCount > 0 && (
              <span className="bg-primary-500 text-white text-xs px-2 py-1 rounded-full min-w-6 h-6 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </div>
          <Button
            variant="link"
            size="sm"
            onClick={resetFilters}
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-4 h-4" />
            Clear All
          </Button>
        </div>

        {/* Scrollable filters container */}
        <div className="max-h-[calc(100vh-200px)] overflow-y-auto pr-2 -mr-2">
          <div className="space-y-6">
            <FilterSection title="Aviation Category" handleFilterChange={() => handleFilterChange("aviation_category", "")}>
              <div className="space-y-3">
                {aviationCategoryChoices && aviationCategoryChoices.map((category) => (
                  <RadioItem
                    key={category.value}
                    id={`aviation-cat-${category.value}`}
                    name="aviation_category"
                    label={category.label}
                    checked={filters.aviation_category === category.value}
                    onChange={() =>
                      handleSingleSelect("aviation_category", category.value)
                    }
                  />
                ))}
              </div>
            </FilterSection>

            {/* Department Filter */}
            <FilterSection title="Pilot types" handleFilterChange={() => handleFilterChange("pilot_type", "")}>
              <Select 
                size='small'
                onChange={(value) => handleFilterChange("pilot_type", value)}
                value={filters.pilot_type}
                options={pilotTypes}
                label="Pilot types"
                 />
            </FilterSection>
            <FilterSection title="Flight Hours" handleFilterChange={() => handleFilterChange("total_flight_hours", "")}>
              <TextField
                label="Total Flight Hours"
                size="small"
                value={filters.total_flight_hours}
                onChange={(e) => handleFilterChange("total_flight_hours", e.target.value)} />
            </FilterSection>
            {/* <FilterSection title="Department">
              <TextField
                select
                fullWidth
                size="small"
                value={filters.department}
                onChange={(e) => handleFilterChange("department", e.target.value)}
                className="bg-gray-50"
                slotProps={{
                  select: {
                    displayEmpty: true,
                  },
                }}
              >
                <MenuItem value="">All Departments</MenuItem>
                {aviationDepartments.map((d) => (
                  <MenuItem key={d.value} value={d.value}>
                    {d.label}
                  </MenuItem>
                ))}
              </TextField>
            </FilterSection> */}

            {/* Job Type Filter - Single Selection */}
            <FilterSection
              title="Job Type"
              handleFilterChange={() => handleFilterChange("job_type", "")}
            >
              {/* <Grid container spacing={1}> */}
                {jobTypes.map((type) => (
                  // <Grid size={{xs: 12, sm: 6}} key={type.value}>
                  <RadioItem
                    key={type.value}
                    id={`job-type-${type.value}`}
                    name="job_type"
                    label={type.label}
                    checked={filters.job_type === type.value}
                    onChange={() => handleSingleSelect("job_type", type.value)}
                  />
                  // </Grid>
                ))}
              {/* </Grid> */}
            </FilterSection>

            {/* Experience Level Filter - Single Selection */}
            <FilterSection title="Experience Level" handleFilterChange={() => handleFilterChange("experience_level", "")}>
              <div className="space-y-3">
                {experienceLevelChoices.map((level) => (
                  <RadioItem
                    key={level.value}
                    id={`exp-level-${level.value}`}
                    name="experience_level"
                    label={level.label}
                    checked={filters.experience_level === level.value}
                    onChange={() =>
                      handleSingleSelect("experience_level", level.value)
                    }
                  />
                ))}
              </div>
            </FilterSection>

            {/* Salary Range Filter */}

            {/* <FilterSection title="Salary Range" handleFilterChange={() => {
              handleFilterChange("min_salary", "")
              handleFilterChange("max_salary", "")
              }}>
              <div className="space-y-3">
                {salaryRanges.map((range) => (
                  <RadioItem
                    key={range.id}
                    id={`salary-${range.id}`}
                    name="salary_range"
                    label={range.label}
                    checked={isSalaryRangeSelected(range)}
                    onChange={() => handleSalaryRangeSelect(range)}
                  />
                ))}

                // Custom Salary Range Inputs
                <div className="pt-3 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Custom Range
                  </p>
                  <div className="grid grid-cols-2 gap-2 mx-2">
                    <div>
                      <label
                        htmlFor="min_salary"
                        className="block text-xs text-gray-500 mb-1"
                      >
                        Min Salary
                      </label>
                      <input
                        type="number"
                        id="min_salary"
                        value={filters.min_salary || ""}
                        onChange={(e) =>
                          handleCustomSalaryChange("min_salary", e.target.value)
                        }
                        placeholder="Min"
                        min={0}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="max_salary"
                        className="block text-xs text-gray-500 mb-1"
                      >
                        Max Salary
                      </label>
                      <input
                        min={0}
                        type="number"
                        id="max_salary"
                        value={filters.max_salary || ""}
                        onChange={(e) =>
                          handleCustomSalaryChange("max_salary", e.target.value)
                        }
                        placeholder="Max"
                        className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Leave empty for no limit
                  </p>
                </div>
              </div>
            </FilterSection> */}

            {/* Work Arrangement Filter - Regular Checkbox */}
            {/* <FilterSection title="Work Arrangement">
              <div className="space-y-3">
                <CheckboxItem
                  id="is_remote"
                  label="Remote Only"
                  checked={filters.is_remote}
                  onChange={() =>
                    handleFilterChange("is_remote", !filters.is_remote)
                  }
                />
              </div>
            </FilterSection> */}
          </div>
        </div>

        {/* Apply Button */}
        {/* <Button
          onClick={applyFilters}
          fullWidth
          className="mt-8 py-3 font-semibold text-base shadow-md hover:shadow-lg transition-all duration-200"
        >
          Apply Filters
        </Button> */}
      </div>
    </aside>
  );
};

export default SideFilters;

// Enhanced FilterSection with smooth animations
const FilterSection = ({ title, children, handleFilterChange }) => (
  <div className="border-t border-gray-200 pt-4 first:border-t-0 first:pt-0 transition-all duration-200">
    <div className="flex justify-between items-start">
      <h4 className="font-semibold mb-3 text-gray-800 flex items-center">
        <span className="bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
          {title}
        </span>
      </h4>
      <Button
        variant="link"
        size="sm"
        onClick={handleFilterChange}
        className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors"
      >
        <X className="w-4 h-4" />
        clear
      </Button>
    </div>
    <div className="transition-all duration-200">{children}</div>
  </div>
);

// Enhanced CheckboxItem with better interaction states (for Work Arrangement)
const CheckboxItem = ({ id, label, checked, onChange }) => (
  <div className="flex items-center group cursor-pointer p-2 rounded-lg transition-all duration-200 hover:bg-gray-50 active:bg-gray-100">
    <div className="relative flex items-center">
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={onChange}
        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 cursor-pointer"
      />
    </div>
    <label
      htmlFor={id}
      className={`ml-3 text-sm transition-all duration-200 cursor-pointer select-none ${
        checked ? "text-gray-900 font-medium" : "text-gray-600"
      } group-hover:text-gray-900`}
    >
      {label}
    </label>
  </div>
);

// New RadioItem component for single selection behavior
const RadioItem = ({ id, name, label, checked, onChange }) => (
  <div
    className="flex items-center group cursor-pointer p-2 rounded-lg transition-all duration-200 hover:bg-gray-50 active:bg-gray-100"
    // id={id}
  >
    <div className="relative flex items-center">
      <input
        type="radio"
        id={id}
        name={name}
        checked={checked}
        onChange={onChange}
        className="h-4 w-4 rounded-full border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 cursor-pointer"
      />
    </div>
    <label
      htmlFor={id}
      className={`ml-3 text-sm transition-all duration-200 cursor-pointer select-none ${
        checked ? "text-gray-900 font-medium" : "text-gray-600"
      } group-hover:text-gray-900`}
    >
      {label}
    </label>
  </div>
);
